import dateHelper from '@utils/dates';
import textHelper from '@utils/textHelper';
import { courseStates, itemTypes } from '@constants/index';
import Item from './Item';

const getInfoString = (course) => {
  const liveAt = course.liveAt || course.startsAt;
  if (!liveAt) return '';
  let infoString = '';
  if (course.state === courseStates.COMPLETED) {
    infoString = `${dateHelper.getDateShort(
      liveAt
    )} • ${dateHelper.getTimeFormatInShortFormWithoutSecs(
      course.lessonDuration
    )}`;
  } else if (course.state === courseStates.LIVE) {
    infoString = `Started on ${dateHelper.formatTime(liveAt)}`;
  }
  infoString = `Starts on ${dateHelper.getDateShort(
    liveAt
  )} • ${dateHelper.formatTime(liveAt)}`;
  return infoString;
};

const isUpcoming = (date) => {
  if (!date) return false;
  const now = new Date();
  return new Date(date).getTime() > now.getTime();
};

const isOngoing = (startsAt, endsAt) => {
  if (!startsAt || !endsAt) return false;
  const now = new Date();
  return (
    new Date(startsAt).getTime() < now.getTime() &&
    new Date(endsAt).getTime() > now.getTime()
  );
};

const isCompleted = (date) => {
  if (!date) return false;
  const now = new Date();
  return new Date(date).getTime() < now.getTime();
};

export const isSummitClass = (data = {}) => data.goal?.uid === 'DCGBT';

class Course {
  constructor(course) {
    this.uid = course.uid;
    this.name = course.name;
    this.thumbnail = course.thumbnail || course.cover_photo || null;
    this.description = course.description || null;
    this.permalink = course.permalink;
    this.relativeLink = course.relative_link;

    this.averageRating = course.avg_rating;
    this.slug = course.slug;
    this.isActive = course.is_active;
    this.totalReviews = course.total_reviews;
    this.totalRatings = course.total_ratings;
    this.forPlus = course.for_plus || course.for_subscription;
    this.forSubscription = course.for_subscription;
    this.livestreamUrl = course.livestream_url;
    this.totalTime = course.total_time;
    this.viewCount = course.view_count;
    this.savedCount = course.saved_count;
    this.previewVideo = course.intro_video;
    this.language =
      course?.language_display || course?.multiple_language_display?.[0];
    this.liveClassCount = course.sessions_count;
    this.isEnrolled = course.is_enrolled;
    this.isBookmarked = course.bookmarked;
    this.isSpecial = course.is_special;
    this.isIconic = course.is_iconic;
    this.enrollmentCount = course.enrollment_count;
    this.secondaryType = course.secondary_type;
    this.courseType = course.programme_type;
    this.isLive = course.is_live;
    this.state = course.state ?? course.status;
    this.liveClassesRemaining = course.sessions_remaining;
    this.itemCount = course.all_item_count || course.item_count;
    this.lessonsWatched = course.lessons_watched;
    this.lessonsCompleted = course.lessons_completed;
    this.startsAt = course.starts_at;
    this.endsAt = course.ends_at;
    this.isUpcoming = isUpcoming(this.startsAt);
    this.isOngoing = isOngoing(this.startsAt, this.endsAt);
    this.isCompleted = course.is_completed || isCompleted(this.endsAt);
    this.quizCount = course.all_item_count - course.sessions_count;
    this.channelDetails = course.channel;
    this.nextTrack = {
      item:
        course.next_track ||
        course.next_session ||
        course.next_class ||
        course.next_item
    };
    this.previousTrack = { item: course.previous_session };
    this.authors = course.authors
      ? course.authors.map((author) => ({ user: author }))
      : [{ user: course.author }];
    this.topicGroups = course.topic_groups
      ? course.topic_groups.map((topicGroup) => ({ topology: topicGroup }))
      : null;
    this.topic = course?.topic_groups?.[0]?.name;
    this.conceptTopology = course?.concept_topology?.name;
    this.coverPhoto = course.cover_photo || course.thumbnail;
    this.colorCode = course.color_code;
    this.goal = course.goal ? { topology: course.goal } : null;
    this.topologies = course.topologies
      ? course.topologies.map((topologyItem) => ({ topology: topologyItem }))
      : null;
    this.goalUid = course.goal_uid || course.goal?.uid || null;
    this.infoString = getInfoString(this);
    this.liveClasses = course.live_classes
      ? course.live_classes.map((itemData) => ({ item: itemData }))
      : null;
    this.tag = course.tag;
    this.unLiveUrl = course.unlive_url; // 'https://player.uacdn.net/liveweb/v1037/index.html';
    this.progress = course.progress;
    if (this.progress) {
      this.progress.nextLessonRelativeLink = this.progress.next_lesson_relative_link;
      this.progress.isCompleted = parseInt(this.progress.percentage, 2) === 100;
    }
    this.conceptLessonCount = course.lesson_count;
    this.ogImage = course.og_image || null;
    this.type = course.type || ''; // in search page, we get type as `collection`
    this.voiceLanguages = course.voiceLanguages;
    this.contentSlideLanguages = course.contentSlideLanguages;
  }
}

const getEntityData = (data) => {
  const course = new Course(data);
  return course;
};

export const getTopicGroup = (data = {}) =>
  data.topicGroups ? data.topicGroups[0] : null;

const getAuthorsName = (authors) => {
  if (!authors) return '';
  const nameText = authors.slice(0, 3).reduce((initial, educator, index) => {
    if (index === 1) return initial + educator.name;
    if (index < 2 && authors.length === 1) return initial + educator.name;
    if (index < 2) return `${initial} ${educator.name}, `;
    return `${initial} and ${authors.length - 2} more`;
  }, '');
  return nameText;
};

const getLink = (data) => {
  if (data.isSpecial) {
    return Item.getLink(data);
  }

  if (
    !data.forPlus &&
    data.progress &&
    !data.progress.isCompleted &&
    data.progress.nextLessonRelativeLink
  ) {
    return data.progress.nextLessonRelativeLink;
  }

  let { slug } = data;
  const { type, forPlus } = data;
  if (!slug) {
    const courseName = data.name?.replace(/\//g, ' ');
    slug = textHelper.getStringWordsJoinedWith(courseName, '-');
  }
  return `/${
    type === itemTypes.POST ? (forPlus && 'class') || 'lesson' : 'course'
  }/${slug}/${data.uid}`;
};

const getLinkPath = (data) => {
  if (data.isSpecial) {
    if (!data.nextTrack || !data.nextTrack.uid) {
      return '/course/[slug]/[courseUID]';
    }
    return '/class/[slug]/[uid]';
  }
  if (
    !data.forPlus &&
    data.progress &&
    !data.progress.isCompleted &&
    data.progress.nextLessonRelativeLink
  ) {
    return '/lesson/[lessonSlug]/[lessonUID]';
  }
  const { type, forPlus } = data;
  return `/${
    type === itemTypes.POST ? (forPlus && 'class') || 'lesson' : 'course'
  }/[slug]/[courseUID]`;
};

export default {
  Course,
  getEntityData,
  getAuthorsName,
  getTopicGroup,
  getLink,
  getLinkPath,
  isSummitClass
};
